import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { API } from "@/assets";
import { useContent } from "@/components/useContent";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";

export const NewsletterSection = () => {
  const content = useContent();
  const heading = content.newsletter_heading || "Early access to new beats, merch drops & label updates.";
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      const res = await fetch(`${API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "Enter a valid email address.");
      }
      setState("done");
    } catch (err) {
      setError(err.message || "Something went wrong.");
      setState("error");
    }
  };

  return (
    <section className="relative py-20 lg:py-24" data-testid="newsletter-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-crystal/30 bg-charcoal px-6 sm:px-12 py-12 sm:py-16 text-center">
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[480px] h-[240px] bg-crystal/25 blur-[100px] rounded-full" aria-hidden="true" />
            <div className="relative">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium">
                VIP Mailing List
              </p>
              <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight text-white leading-tight">
                {heading}
              </h2>
              <p className="mt-4 text-sm text-ice/60 max-w-md mx-auto leading-relaxed">
                Join the cold-blooded inner circle. First dibs on exclusives before they ever hit the store.
              </p>

              {state === "done" ? (
                <div className="mt-8 inline-flex items-center gap-2.5 text-venom" data-testid="newsletter-success">
                  <CheckCircle2 size={20} />
                  <span className="font-semibold text-sm">You&apos;re on the list. Watch your inbox.</span>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-form">
                  <div className="relative flex-1">
                    <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ice/35" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full bg-obsidian/80 border border-white/12 rounded-full pl-11 pr-4 py-3.5 text-sm text-ice placeholder:text-ice/35 focus:outline-none focus:border-venom/70 focus:ring-2 focus:ring-venom/20 transition-all duration-300"
                      data-testid="newsletter-email-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={state === "sending"}
                    className="inline-flex items-center justify-center gap-2 bg-venom text-obsidian font-bold text-sm px-7 py-3.5 rounded-full hover:glow-venom hover:scale-[1.03] active:scale-95 disabled:opacity-60 transition-all duration-300"
                    data-testid="newsletter-submit-button"
                  >
                    {state === "sending" ? <Loader2 size={16} className="animate-spin" /> : null}
                    {state === "sending" ? "Joining…" : "Get Early Access"}
                  </button>
                </form>
              )}
              {error && (
                <p className="mt-3 text-sm text-red-400" role="alert" data-testid="newsletter-error">{error}</p>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
